import { RefObject, useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { ReportEditorRef } from '../../report-editor/ReportEditor';
import {
  downloadReportPdfHtml2Pdf,
  openPdfPreviewInNewTab,
  printReport,
} from '../../report-editor/services/pdf-export-html2pdf.service';
import {
  ReportTemplate,
  ReportBindingData,
  SignerEntry,
} from '../../report-editor/types/template.types';
import { JSONContent } from '@tiptap/react';

// -----------------------------------------------------------------------------
interface UsePdfActionsParams {
  editorRef: RefObject<ReportEditorRef>;
  template: ReportTemplate | null | undefined;
  content: JSONContent | undefined;
  orderId: string | undefined;
  signers?: SignerEntry[];
  /** Binding data used to populate the patient info box on pages 2+ */
  bindingData?: ReportBindingData;
}

// -----------------------------------------------------------------------------
export const usePdfActions = ({
  editorRef,
  template,
  content,
  orderId,
  signers = [],
  bindingData,
}: UsePdfActionsParams) => {
  const { t } = useTranslation(['lims']);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ---------------------------------------------------------------------------
  const handlePreviewPDF = useCallback(async () => {
    const editorElement = editorRef.current?.getEditorElement();
    if (!editorElement || !template || !content) {
      return;
    }

    setPdfLoading(true);
    try {
      await openPdfPreviewInNewTab(editorElement, {
        content,
        template,
        signers,
        includePageNumbers: true,
        bindingData,
      });
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      notifications.show({
        title: t('reportEditor.notifications.exportError'),
        message: String(error),
        color: 'red',
      });
    } finally {
      setPdfLoading(false);
    }
  }, [editorRef, template, content, signers, bindingData, t]);

  // ---------------------------------------------------------------------------
  const handleExportPDF = useCallback(async () => {
    const editorElement = editorRef.current?.getEditorElement();
    if (!editorElement || !template || !content) {
      return;
    }

    setPdfLoading(true);
    try {
      const filename = `report-${orderId || 'draft'}-${new Date().toISOString().split('T')[0]}`;

      await downloadReportPdfHtml2Pdf(editorElement, {
        content,
        template,
        signers,
        filename,
        includePageNumbers: true,
        bindingData,
      });

      notifications.show({
        title: t('reportEditor.notifications.exportSuccess'),
        message: '',
        color: 'green',
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      notifications.show({
        title: t('reportEditor.notifications.exportError'),
        message: String(error),
        color: 'red',
      });
    } finally {
      setPdfLoading(false);
    }
  }, [editorRef, content, template, signers, bindingData, orderId, t]);

  // ---------------------------------------------------------------------------
  const handlePrint = useCallback(() => {
    const editorElement = editorRef.current?.getEditorElement();
    if (!editorElement) {
      return;
    }
    try {
      printReport(editorElement);
    } catch (error) {
      console.error('Error printing:', error);
      notifications.show({
        title: t('reportEditor.notifications.exportError'),
        message: String(error),
        color: 'red',
      });
    }
  }, [editorRef, t]);

  // ---------------------------------------------------------------------------
  return { pdfLoading, handlePreviewPDF, handleExportPDF, handlePrint };
};
